import React from 'react'
import { MapPinIcon } from '@heroicons/react/24/solid'
import PropTypes from 'prop-types'
import { PhoneIcon, EnvelopeIcon, CalendarIcon } from '@heroicons/react/24/outline'
import { normalizeSocialLinks } from '../../../utils/social'
import { buildCloudinaryUrl, buildCloudinarySrcSet } from '../../../utils/cloudinaryImage'
import PWAInstallButton from '../../../components/pwa/PWAInstallButton'
import useCardPWA from '../../../pwa/useCardPWA'

function ServicesTemplate({ vCard, template }) {
  const theme = vCard.theme || {
    primaryColor: '#10b981',
    accentColor: '#6b7280',
    font: 'Inter'
  }

  const socialLinks = normalizeSocialLinks(vCard.social || {})
  useCardPWA(vCard)
  const services = (() => {
    if (!vCard.services) return []
    if (Array.isArray(vCard.services)) return vCard.services
    if (typeof vCard.services === 'object') {
      return Object.entries(vCard.services).map(([id, service]) => ({ id, ...service }))
    }
    return []
  })()

  return (
    <div 
      className="services-template w-full sm:max-w-md mx-auto bg-white rounded-2xl shadow-xl overflow-hidden min-h-screen"
      style={{ fontFamily: theme.font }}
    >
      {/* Header */}
      <div className="relative h-40" style={{ backgroundImage: 'linear-gradient(to bottom right, #6242BF, #05C0FB)' }}>
        <div className="absolute inset-0 bg-black bg-opacity-20"></div>
        <div className="absolute bottom-0 left-0 right-0 p-6">
          <div className="flex items-end">
            <img
              src={buildCloudinaryUrl(vCard.photoUrl || 'https://via.placeholder.com/80x80/e2e8f0/64748b?text=Foto', { w: 120, h: 120, crop: 'fill' })}
              srcSet={buildCloudinarySrcSet(vCard.photoUrl || 'https://via.placeholder.com/80x80/e2e8f0/64748b?text=Foto', [60, 90, 120])}
              sizes="(max-width: 768px) 90px, 120px"
              alt={vCard.name}
              className="w-20 h-20 rounded-full border-4 border-white shadow-lg object-cover"
              loading="lazy"
            />
            <div className="ml-4 mb-2">
              <h1 className="text-xl font-bold text-white">{vCard.name}</h1>
              <p className="text-green-100 text-sm">{vCard.position}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-6 py-8">
        {/* Bio */}
        {vCard.bio && (
          <div className="mb-6">
            <p className="text-gray-700 text-sm leading-relaxed">{vCard.bio}</p>
          </div>
        )}

        {/* Services */}
        {services.length > 0 && (
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Servicios</h3>
            <div className="space-y-3">
              {services.map((service) => (
                <div key={service.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex justify-between items-start mb-2">
                    <h4 className="font-medium text-gray-900">{service.name || service.title}</h4>
                    <span className="text-lg font-bold" style={{ color: theme.primaryColor }}>
                      {service.price != null ? `Bs. ${service.price}` : ''}
                    </span>
                  </div>
                  <p className="text-gray-600 text-sm">{service.desc}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Contact Info */}
        <div className="space-y-3 mb-6">
          {vCard.phone && (
            <a 
              href={`tel:${vCard.phone}`}
              className="flex items-center text-gray-700 hover:text-green-600 transition-colors"
            >
              <PhoneIcon className="w-5 h-5 mr-3" style={{ color: theme.primaryColor }} />
              <span>{vCard.phone}</span>
            </a>
          )}
          
          {vCard.email && (
            <a 
              href={`mailto:${vCard.email}`}
              className="flex items-center text-gray-700 hover:text-green-600 transition-colors"
            >
              <EnvelopeIcon className="w-5 h-5 mr-3" style={{ color: theme.primaryColor }} />
              <span>{vCard.email}</span>
            </a>
          )}
        </div>

        {/* Action Buttons */}
        <div className="space-y-3 pb-10">
          <button
            className="w-full py-3 px-4 rounded-xl font-medium text-white transition-all hover:scale-105 flex items-center justify-center"
            style={{ backgroundColor: theme.primaryColor }}
            onClick={() => {
              if (!socialLinks.whatsapp) return
              const msg = 'Hola, me gustaría agendar una cita'
              const waUrl = `${socialLinks.whatsapp}${socialLinks.whatsapp.includes('?') ? '&' : '?'}text=${encodeURIComponent(msg)}`
              window.open(waUrl, '_blank')
            }}
          >
            <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.645 3.488"/>
            </svg>
            Agendar Cita
          </button>
          
          <button
            className="w-full py-3 px-4 rounded-xl font-medium text-white transition-all hover:scale-105"
            style={{ backgroundColor: theme.accentColor }}
            onClick={() => window.open(`tel:${vCard.phone}`, '_self')}
          >
            Llamar Ahora
          </button>
        </div>
      </div>
      <PWAInstallButton vCard={vCard} />
    </div>
  )
}

ServicesTemplate.propTypes = {
  vCard: PropTypes.object.isRequired,
  template: PropTypes.object.isRequired,
}

export default ServicesTemplate
